/**
 * MedicationAgenda — 24-hour day view of medication occurrences (spec §9.4).
 *
 * Own agenda (client): renders the full MedicationCard — due card, mark
 * taken with optional note, proof entry, upload status, bounded undo.
 *
 * Linked-client agenda (caregiver): pass clientId + readOnly; the same day
 * view is rendered without any mutating action. A caregiver can never mark
 * a client's intake as performed from here (spec §9.3).
 */
import { Card, EmptyState, ErrorState, Skeleton } from '@/components/ui'
import { PillIcon } from '@/components/icons'
import {
  MedicationCard,
  MedicationGroupList,
  formatFullDutchDay,
  useMedicationDay,
} from '@/features/medication'

export type MedicationAgendaProps = {
  /** Local ISO date (yyyy-mm-dd). */
  date: string
  /** When set, shows the linked client's day (caregiver context). */
  clientId?: string
  /** Hides all mutating actions; forced when clientId is set. */
  readOnly?: boolean
}

export function MedicationAgenda({ date, clientId, readOnly = false }: MedicationAgendaProps) {
  if (!clientId) {
    return (
      <section className="client-agenda" aria-label={`Medicatie-agenda voor ${formatFullDutchDay(date)}`}>
        <h2 className="client-agenda-title">{formatFullDutchDay(date)}</h2>
        <MedicationCard date={date} readOnly={readOnly} />
      </section>
    )
  }
  return <LinkedClientAgenda date={date} clientId={clientId} />
}

function LinkedClientAgenda({ date, clientId }: { date: string; clientId: string }) {
  const { status, items, error, refresh } = useMedicationDay(date, clientId)

  return (
    <section className="client-agenda" aria-label={`Medicatie-agenda voor ${formatFullDutchDay(date)}`}>
      <h2 className="client-agenda-title">{formatFullDutchDay(date)}</h2>
      {status === 'loading' ? <Skeleton lines={3} /> : null}
      {status === 'unavailable' ? (
        <Card padding="md">
          <p className="medication-muted">
            Cloud niet ingesteld. De medicatie-agenda kan nu niet worden opgehaald.
          </p>
        </Card>
      ) : null}
      {status === 'error' ? (
        <ErrorState error={error ?? undefined} onRetry={() => void refresh()} />
      ) : null}
      {status === 'ready' ? (
        <Card padding="sm">
          {items.length === 0 ? (
            <EmptyState
              icon={<PillIcon size={24} />}
              title="Geen medicatie gepland"
              body="Voor deze dag staan er geen medicatiemomenten in het schema van deze cliënt."
            />
          ) : (
            <MedicationGroupList items={items} />
          )}
          <p className="medication-muted client-agenda-readonly-note">
            Alleen-lezen overzicht. De cliënt registreert zelf de inname; een begeleider kan dit
            niet voor de cliënt doen.
          </p>
        </Card>
      ) : null}
    </section>
  )
}
